import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { stamp } from '../lib/format';
import { C, ink, surface } from '../lib/theme';
import type { AppRole, AppUser, Me } from '../lib/types';
import { Avatar } from '../components/Avatar';
import { ErrorBanner, PrimaryButton, SectionHeader, Skeleton } from '../components/primitives';
import { useToast } from '../components/Toast';

/**
 * Gestão de acesso ao ambiente. Convidar é o ato de autorizar: a linha em
 * revops_app_users É a permissão de login — o requireAuth casa pelo email no
 * primeiro acesso (a pessoa nem precisa ter conta no Supabase ainda).
 *
 * Mesma linguagem visual da tela de Motivos (§7.6 do PRD): rota própria de
 * admin, tabela simples e form inline, sem modal.
 */
export function UsersAdmin({ me }: { me: Me }) {
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => api.listUsers(), []);
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('member');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function invite() {
    const value = email.trim();
    if (!value) { toast('Informe o email de quem vai receber acesso.', 'error'); return; }
    setSaving(true);
    try {
      const created = await api.inviteUser(value, role);
      toast(`${created.email} agora tem acesso a este ambiente.`, 'ok');
      setEmail(''); setRole('member'); setAdding(false);
      reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao autorizar o acesso.', 'error');
    } finally { setSaving(false); }
  }

  async function patch(u: AppUser, body: { role?: AppRole; active?: boolean }) {
    setBusyId(u.id);
    try {
      await api.updateUser(u.id, body);
      reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao atualizar.', 'error');
    } finally { setBusyId(null); }
  }

  async function remove(u: AppUser) {
    setBusyId(u.id);
    try {
      await api.removeUser(u.id);
      toast(`Acesso de ${u.email} revogado.`, 'info');
      reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao revogar.', 'error');
    } finally { setBusyId(null); }
  }

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 13, padding: '9px 12px',
    borderRadius: 10, border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)',
    color: C.fg, outline: 'none',
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '30px 30px 90px' }}>
      <Link to="/fila" style={{ fontSize: 12.5, color: ink(0.45), textDecoration: 'none' }}>← voltar pro Hub</Link>

      <div style={{ marginTop: 18 }}>
        <SectionHeader
          title="Usuários e acessos"
          right={!adding ? <PrimaryButton onClick={() => setAdding(true)}>+ Dar acesso</PrimaryButton> : undefined}
        />
      </div>

      {adding && (
        <div style={{ ...surface, padding: 18, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void invite(); }}
            placeholder="email@kory.one" style={input} autoFocus
          />
          <div style={{ display: 'flex', gap: 4, background: ink(0.06), borderRadius: 10, padding: 3, maxWidth: 280 }}>
            {(['member', 'admin'] as const).map((r) => (
              <button key={r} type="button" onClick={() => setRole(r)} style={{
                flex: 1, border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 700,
                padding: '7px 0', borderRadius: 8,
                background: role === r ? ink(0.13) : 'transparent',
                color: role === r ? C.fg : ink(0.5),
              }}>{r === 'member' ? 'Usuário' : 'Administrador'}</button>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: ink(0.4), lineHeight: 1.5 }}>
            {role === 'admin'
              ? 'Administrador também gerencia esta lista — pode convidar, promover e revogar acessos.'
              : 'Usuário audita a fila normalmente, mas não enxerga nem altera esta lista.'}
            {' '}A pessoa não precisa ter conta ainda: ela cria a dela no primeiro login com este email.
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 4 }}>
            <PrimaryButton onClick={invite} disabled={saving}>{saving ? 'autorizando…' : 'Autorizar acesso'}</PrimaryButton>
            <button type="button" onClick={() => { setAdding(false); setEmail(''); setRole('member'); }} style={{
              border: `1px solid ${ink(0.14)}`, background: 'transparent', color: ink(0.55), cursor: 'pointer',
              font: 'inherit', fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 12,
            }}>Cancelar</button>
          </div>
        </div>
      )}

      {loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[0, 1].map((i) => <Skeleton key={i} height={58} radius={12} />)}</div>}
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {data && (
        <div style={{ ...surface, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 90px 90px', gap: 14, padding: '12px 18px',
            borderBottom: `1px solid ${ink(0.08)}`, fontSize: 10, fontWeight: 800,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: ink(0.35),
          }}>
            <span>Pessoa</span>
            <span>Papel</span>
            <span style={{ textAlign: 'center' }}>Ativo</span>
            <span style={{ textAlign: 'right' }}>Ações</span>
          </div>

          {data.map((u) => {
            const isMe = u.email === me.email;
            const busy = busyId === u.id;
            return (
              <div key={u.id} style={{
                display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 90px 90px', gap: 14,
                padding: '13px 18px', borderBottom: `1px solid ${ink(0.05)}`, alignItems: 'center',
                opacity: u.active ? (busy ? 0.5 : 1) : 0.45,
              }}>
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar url={u.avatar_url} email={u.email} size={30} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 650, color: ink(0.9), display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                      {isMe && (
                        <span style={{
                          fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                          padding: '2px 6px', borderRadius: 6, background: 'rgba(255,144,112,0.16)',
                          color: C.accent, flex: 'none',
                        }}>você</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: ink(0.35), marginTop: 2 }}>
                      {u.has_signed_in
                        ? `último acesso ${u.last_login_at ? stamp(u.last_login_at) : '—'}`
                        : 'ainda não entrou'}
                      {u.invited_by_email ? ` · convidado por ${u.invited_by_email}` : ''}
                    </div>
                  </div>
                </div>

                <div>
                  <select
                    value={u.role} disabled={busy || isMe}
                    onChange={(e) => patch(u, { role: e.target.value as AppRole })}
                    style={{
                      ...input, padding: '6px 9px', fontSize: 12.5, cursor: isMe ? 'not-allowed' : 'pointer',
                      opacity: isMe ? 0.5 : 1,
                    }}
                    title={isMe ? 'Você não pode alterar o próprio papel.' : undefined}
                  >
                    <option value="member">Usuário</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button" role="switch" aria-checked={u.active} disabled={busy || isMe}
                    onClick={() => patch(u, { active: !u.active })}
                    title={isMe ? 'Você não pode desativar o próprio acesso.' : u.active ? 'Desativar acesso' : 'Reativar acesso'}
                    style={{
                      width: 38, height: 21, borderRadius: 99, border: 'none', padding: 0, position: 'relative',
                      cursor: isMe || busy ? 'not-allowed' : 'pointer',
                      background: u.active ? C.accent : ink(0.14),
                      opacity: isMe ? 0.5 : 1, transition: 'background 0.2s',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3, left: u.active ? 20 : 3, width: 15, height: 15,
                      borderRadius: '50%', background: '#fff', transition: 'left 0.2s ease',
                    }} />
                  </button>
                </div>

                <div style={{ textAlign: 'right' }}>
                  {!isMe && (
                    <button type="button" onClick={() => remove(u)} disabled={busy} style={{
                      border: 'none', background: 'none', cursor: busy ? 'wait' : 'pointer', font: 'inherit',
                      fontSize: 11.5, color: ink(0.4),
                    }}>revogar</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 11.5, color: ink(0.32), marginTop: 14, lineHeight: 1.6 }}>
        <strong style={{ color: ink(0.5) }}>Desativar</strong> mantém a pessoa na lista e no histórico de auditoria, só
        bloqueia o login. <strong style={{ color: ink(0.5) }}>Revogar</strong> apaga a autorização — o que ela já auditou
        continua registrado. O ambiente nunca fica sem administrador: o último admin ativo não pode ser rebaixado nem removido.
      </div>
    </div>
  );
}
